import React, { useEffect, useRef, useState } from "react";
import "./AIAnalysis.css";
import apiService from "../services/apiService";
import CaseForm from "./CaseForm";
import CaseSuccess from "./CaseSuccess";
import EmailReplyDraft from "./EmailReplyDraft";
import {
    CASE_TYPE_OPTIONS,
    CASE_TYPE_FIELDS,
    STANDARD_HEADER_FIELDS,
    getFieldsForCaseType,
    getEmptyFieldsForCaseType,
    getFieldsToExtractPayload,
    getChildObjectName,
    getSalesforceMappingForField,
} from "../data/caseTypeConfig";

/* global Office */

const INITIAL_FIELDS = {
    subject: "",
    contactId: "",
    policyId: "",
    caseType: "",
    priority: "",
    description: "",
};

const INITIAL_SOURCES = {
    subject: "email",
    contactId: "sf",
    policyId: "sf",
    caseType: "ai",
    priority: "ai",
    description: "ai",
};

const CASE_LEVEL_FIELD_MAPPINGS = [
    { key: "subject", sfObjectName: "Case", sfApiName: "Subject" },
    { key: "contactId", sfObjectName: "Case", sfApiName: "ContactId" },
    { key: "priority", sfObjectName: "Case", sfApiName: "Priority" },
    { key: "description", sfObjectName: "Case", sfApiName: "Description" },
    { key: "caseType", sfObjectName: "Case", sfApiName: "Type" },
];

const hasSalesforceValue = (value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim() !== "";
    return true;
};

const mergeSalesforceValues = (existingValue, nextValue, apiName) => {
    if (!hasSalesforceValue(existingValue)) return nextValue;
    if (!hasSalesforceValue(nextValue)) return existingValue;
    if (existingValue === nextValue) return existingValue;

    if (Array.isArray(existingValue) || Array.isArray(nextValue)) {
        return [
            ...(Array.isArray(existingValue) ? existingValue : [existingValue]),
            ...(Array.isArray(nextValue) ? nextValue : [nextValue]),
        ];
    }

    const separator = apiName === "Insured_Name__c" ? " " : "; ";
    return `${existingValue}${separator}${nextValue}`;
};

const assignSalesforceField = (target, apiName, value) => {
    if (!hasSalesforceValue(value)) return;
    target[apiName] = mergeSalesforceValues(target[apiName], value, apiName);
};

const getOutlookAnalysisMetadata = (mailboxContext = {}, selectedMessageId = "") => {

    try {
        const mailbox = Office.context?.mailbox;
        const item = mailbox?.item;
        const contextMatchesItem = !selectedMessageId || selectedMessageId === mailboxContext.messageId;

        return {
            messageId: selectedMessageId || mailboxContext.messageId || item?.internetMessageId || item?.itemId || "",
            // The parent resolves this to the owner of a shared mailbox.
            userEmail: contextMatchesItem
                ? mailboxContext.userEmail || mailbox?.userProfile?.emailAddress || ""
                : mailbox?.userProfile?.emailAddress || "",
        };
    } catch (error) {
        console.log("Failed to read Outlook analysis metadata", error);
        return { messageId: "", userEmail: "" };
    }
};

// Module-level cache to persist state across tab switches
let cachedAiState = {
    analyzed: false,
    fields: INITIAL_FIELDS,
    sources: INITIAL_SOURCES,
    submitted: false,
    caseResult: null,
    followupDraft: "",
    accountId: null,
    messageId: null,
    attachments: [],
    analyseAttachments: false,
};

const AIAnalysis = ({
    sfContext,
    policies,
    sfAuthError,
    sfSession,
    refreshSfToken,
    detectedCaseType,
    currentMessageId: selectedMessageId,
    mailboxContext,
}) => {
    const { messageId: outlookMessageId } = getOutlookAnalysisMetadata(mailboxContext, selectedMessageId);
    const currentMessageId = selectedMessageId || outlookMessageId || "";
    const currentMessageIdRef = useRef(currentMessageId);
    currentMessageIdRef.current = currentMessageId;

    const isCurrentMessage = (messageId) => currentMessageIdRef.current === messageId;

    const [loading, setLoading] = useState(false);
    const [analyzed, setAnalyzed] = useState(cachedAiState.analyzed);
    const [fields, setFields] = useState(cachedAiState.fields);
    const [fieldSources, setFieldSources] = useState(cachedAiState.sources);
    const [submitted, setSubmitted] = useState(cachedAiState.submitted);
    const [error, setError] = useState(null);
    const [creatingCase, setCreatingCase] = useState(false);
    const [caseResult, setCaseResult] = useState(cachedAiState.caseResult);
    const [followupDraft, setFollowupDraft] = useState(cachedAiState.followupDraft);
    const [draftLoading, setDraftLoading] = useState(false);
    const [accountId, setAccountId] = useState(cachedAiState.accountId);
    const [attachments, setAttachments] = useState(cachedAiState.attachments);
    const [analyseAttachments, setAnalyseAttachments] = useState(cachedAiState.analyseAttachments);
    const [isDraftOpen, setIsDraftOpen] = useState(false);

    useEffect(() => {
        // Reset cached AI output when the selected email or Salesforce account changes.
        if (sfContext?.accountId !== cachedAiState.accountId || currentMessageId !== cachedAiState.messageId) {
            handleStartOver();
            setIsDraftOpen(false);
            cachedAiState.accountId = sfContext?.accountId;
            cachedAiState.messageId = currentMessageId;
            setAccountId(sfContext?.accountId);
        }
    }, [currentMessageId, sfContext?.accountId, policies]);

    useEffect(() => {
        const contacts = sfContext?.contacts || [];
        if (contacts.length > 0) {
            setFields((prev) => {
                if (prev.contactId && contacts.some((c) => c.contactId === prev.contactId)) {
                    return prev;
                }
                if (sfContext.contactId && contacts.some((c) => c.contactId === sfContext.contactId)) {
                    return { ...prev, contactId: sfContext.contactId };
                }
                return { ...prev, contactId: "" };
            });
        }
    }, [sfContext?.contacts, sfContext?.contactId]);

    useEffect(() => {
        // If we are already submitted but the draft wasn't finished, try generating again
        if (submitted && !followupDraft && !loading && !creatingCase) {
            generateFollowupDraft();
        }
    }, []);

    // Update the cache whenever state changes
    const updateAnalyzed = (val) => {
        cachedAiState.analyzed = val;
        setAnalyzed(val);
    };

    const updateFields = (fieldUpdater) => {
        setFields((prev) => {
            const next = typeof fieldUpdater === "function" ? fieldUpdater(prev) : fieldUpdater;
            cachedAiState.fields = next;
            return next;
        });
    };

    const updateSources = (key, source) => {
        setFieldSources((prev) => {
            const next = { ...prev, [key]: source };
            cachedAiState.sources = next;
            return next;
        });
    };

    const updateSubmitted = (val) => {
        cachedAiState.submitted = val;
        console.log("checking fields", fields)
        setSubmitted(val);
    };

    const updateFollowupDraft = (val) => {
        cachedAiState.followupDraft = val;
        setFollowupDraft(val);
    };

    const updateAttachments = (val) => {
        cachedAiState.attachments = val;
        setAttachments(val);
    };

    const updateAnalyseAttachments = (val) => {
        cachedAiState.analyseAttachments = val;
        setAnalyseAttachments(val);
    };

    const handleAnalyze = async (forceAnalyseAttachments = false) => {
        const metadata = getOutlookAnalysisMetadata(mailboxContext, selectedMessageId);
        const requestMessageId = currentMessageId || metadata.messageId || "";
        metadata.messageId = requestMessageId;

        const shouldAnalyseAttachments = (forceAnalyseAttachments === true) || analyseAttachments;
        if (forceAnalyseAttachments === true) {
            updateAnalyseAttachments(true);
        }

        setLoading(true);
        try {
            setError(null);

            if (!metadata.messageId) {

                setError("Unable to identify this Outlook message. Please reopen the email and try again.");
                return;
            }

            // Get email body and prepend subject for better AI context
            let emailBody = "";
            try {
                const item = Office.context?.mailbox?.item;
                if (item) {
                    const bodyText = await new Promise((resolve) => {
                        item.body.getAsync("text", (result) => {
                            resolve(result.status === "succeeded" ? result.value : "");
                        });
                    });
                    emailBody = `Subject: ${item.subject || ""}\n\nBody:\n${bodyText}`;
                }
            } catch (e) {
                console.log("Office.js not available, using empty body");
            }

            // If "Analyse Attachments" is enabled, fetch attachment contents
            let attachmentPayloads = [];
            if (shouldAnalyseAttachments) {
                try {
                    const item = Office.context?.mailbox?.item;
                    if (item && item.attachments && item.attachments.length > 0) {
                        attachmentPayloads = await Promise.all(
                            item.attachments.map(async (att) => {
                                try {
                                    const content = await new Promise((resolve, reject) => {
                                        item.getAttachmentContentAsync(att.id, (result) => {
                                            if (result.status === Office.AsyncResultStatus.Succeeded) {
                                                resolve(result.value.content);
                                            } else {
                                                reject(new Error(result.error.message));
                                            }
                                        });
                                    });
                                    return {
                                        name: att.name,
                                        contentType: att.contentType,
                                        content: content,
                                    };
                                } catch (err) {
                                    console.error(`Failed to fetch attachment ${att.name}:`, err);
                                    return null;
                                }
                            })
                        );
                        attachmentPayloads = attachmentPayloads.filter(Boolean);
                    }
                } catch (e) {
                    console.log("Error fetching attachments for analysis", e);
                }
            }

                        const data = await apiService.analyzeEmailDetailed({
                emailBody,
                messageId: metadata.messageId,
                userEmail: metadata.userEmail,
                caseType: detectedCaseType || "",
                fieldsToExtract: JSON.stringify(getFieldsToExtractPayload(detectedCaseType || "")),
                attachments: shouldAnalyseAttachments ? attachmentPayloads : undefined,
            });

            if (!isCurrentMessage(requestMessageId)) {
                return;
            }

            // Get attachments metadata

            let attachmentMetadata = [];
            try {
                const item = Office.context?.mailbox?.item;
                if (item && item.attachments) {
                    attachmentMetadata = item.attachments.map(att => ({
                        id: att.id,
                        name: att.name,
                        contentType: att.contentType,
                        size: att.size,
                        attachmentType: att.attachmentType,
                        selected: true // default select all
                    }));
                }
            } catch (e) {
                console.log("Error fetching attachment metadata", e);
            }
            updateAttachments(attachmentMetadata);

            console.log("checking data", data)

            // Determine case type: use detected from context analysis,
            // or fall back to whatever the detailed analysis returned
            const resolvedCaseType = detectedCaseType || data.caseType || "";

            // Build dynamic field set from the case type config
            const typeSpecificEmpty = getEmptyFieldsForCaseType(resolvedCaseType);
            const standardHeaderEmpty = {};
            STANDARD_HEADER_FIELDS.forEach((f) => { standardHeaderEmpty[f.key] = ""; });

            // Merge: start with common fields, add standard header, add type-specific
            const mergedFields = {
                ...INITIAL_FIELDS,
                ...standardHeaderEmpty,
                ...typeSpecificEmpty,
                caseType: resolvedCaseType,
                priority: data.priority || "",
                description: data.description || "",
                subject: data.subject || "",
            };

            // Populate every field the AI returned
            Object.keys(mergedFields).forEach((key) => {
                if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
                    mergedFields[key] = data[key];
                }
            });

            // Build sources: all AI-returned fields get "ai" source
            const mergedSources = { ...INITIAL_SOURCES };
            [...STANDARD_HEADER_FIELDS, ...getFieldsForCaseType(resolvedCaseType)].forEach((f) => {
                mergedSources[f.key] = "ai";
            });

            updateFields(mergedFields);

            // Set subject, contact, and policy from Office.js and Salesforce Context
            try {
                const item = Office.context?.mailbox?.item;
                if (item) {
                    const contacts = sfContext.contacts || [];
                    let defaultContactId = "";

                    if (contacts.length > 0) {
                        // 1. Try to match by sender's email address first
                        const fromEmail = item.from?.emailAddress?.toLowerCase();
                        let matched = fromEmail ? contacts.find(c => c.email && c.email.toLowerCase() === fromEmail) : null;

                        // 2. If no email match, try to match by extracted insured name from AI
                        if (!matched) {
                            const firstName = (data.insuredFirstName || "").toLowerCase().trim();
                            const lastName = (data.insuredLastName || "").toLowerCase().trim();
                            if (firstName || lastName) {
                                matched = contacts.find(c => {
                                    const cName = (c.name || "").toLowerCase();
                                    const matchesFirst = firstName ? cName.includes(firstName) : true;
                                    const matchesLast = lastName ? cName.includes(lastName) : true;
                                    return matchesFirst && matchesLast;
                                });
                            }
                        }

                        // No fallback to contacts[0]: keep empty so user sees "Select contact..."
                        defaultContactId = matched ? matched.contactId : "";
                    }

                    // 4. Try to match policy from email by AI extracted policy number or email context
                    const rawExtractedPolicy = (data.policyNumber || data.policyNumbers || emailInfo.policyMentioned || "").trim().toLowerCase();
                    const cleanExtracted = rawExtractedPolicy.replace(/[^a-z0-9]/gi, "");
                    let matchedPolicy = null;
                    if (cleanExtracted && policies && policies.length > 0) {
                        matchedPolicy = policies.find(p => {
                            const pNum = (p.policyNumber || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
                            return pNum && (pNum === cleanExtracted || pNum.includes(cleanExtracted) || cleanExtracted.includes(pNum));
                        });
                    }
                    // No fallback to firstPolicy: keep null so user sees "Select policy..."
                    const selectedPolicyObj = matchedPolicy || null;

                    updateFields((prev) => ({
                        ...prev,
                        subject: item.subject || prev.subject,
                        contactId: defaultContactId || (contacts.some(c => c.contactId === prev.contactId) ? prev.contactId : ""),
                        policyId: selectedPolicyObj ? (selectedPolicyObj.policyId || selectedPolicyObj.id) : (policies?.some(p => (p.policyId || p.id) === prev.policyId) ? prev.policyId : ""),
                    }));
                }
            } catch (e) { /* ignore */ }

            updateAnalyzed(true);
            setFieldSources(mergedSources);
            cachedAiState.sources = mergedSources;
        } catch (error) {
            console.error("Analysis failed:", error);
            setError("Backend server not working. Please check if the backend is running.");
        } finally {
            if (isCurrentMessage(requestMessageId)) {
                setLoading(false);
            }
        }
    };

    const handleCreateCase = async () => {
        const requestMessageId = currentMessageId || getOutlookAnalysisMetadata(mailboxContext, selectedMessageId).messageId || "";

        setCreatingCase(true);
        setError(null);

        if (sfAuthError) {
            setError(`Salesforce authentication failed: ${sfAuthError.error || "Access restricted"}. Case creation is disabled.`);
            setCreatingCase(false);
            return;
        }

        try {
            let token = sfSession?.accessToken;
            let instanceUrl = sfSession?.instanceUrl;

            if (!token || !instanceUrl) {
                // Try refreshing the token
                const refreshed = await refreshSfToken?.();
                if (refreshed) {
                    token = refreshed.accessToken;
                    instanceUrl = refreshed.instanceUrl;
                } else {
                    setError("Missing Salesforce authentication. Please retry.");
                    setCreatingCase(false);
                    return;
                }
            }

            // Build payload with Salesforce API names only.
            // Unmapped frontend fields are intentionally skipped for case creation,
            // but remain in `fields` for follow-up email generation.
            const caseType = fields.caseType || "";
            const childObjectName = getChildObjectName(caseType);
            const configuredFieldDefinitions = [
                ...CASE_LEVEL_FIELD_MAPPINGS,
                ...STANDARD_HEADER_FIELDS,
                ...getFieldsForCaseType(caseType),
            ];

            const commonFields = {};
            const childFields = {};

            for (const fieldDefinition of configuredFieldDefinitions) {
                let value = fields[fieldDefinition.key];
                const mapping = fieldDefinition.sfApiName
                    ? {
                        objectName: fieldDefinition.sfObjectName || "Case",
                        apiName: fieldDefinition.sfApiName,
                    }
                    : getSalesforceMappingForField(fieldDefinition, caseType);

                if (!mapping?.apiName || !hasSalesforceValue(value)) continue;

                // Convert "Yes"/"No" strings to boolean for Salesforce Checkbox fields
                if (fieldDefinition.sfBoolean && typeof value === "string") {
                    value = value.toLowerCase() === "yes";
                }

                // Convert string to number if Salesforce field expects a numeric value
                if (fieldDefinition.sfNumber && value) {
                    const cleanValue = value.toString().replace(/[^0-9.]/g, "");
                    const parsed = parseFloat(cleanValue);
                    if (!isNaN(parsed)) {
                        value = parsed;
                    }
                }

                // Slice last 4 characters if sfLast4 modifier is enabled
                if (fieldDefinition.sfLast4 && value) {
                    const stringVal = value.toString().trim();
                    value = stringVal.slice(-4);
                }

                // Flatten arrays to strings for Salesforce primitive fields
                if (Array.isArray(value)) {
                    value = value.map(v => v.toString().trim()).filter(Boolean).join(", ");
                }

                if (mapping.objectName === "Case") {
                    assignSalesforceField(commonFields, mapping.apiName, value);
                } else if (mapping.objectName === childObjectName) {
                    assignSalesforceField(childFields, mapping.apiName, value);
                }
            }

            // Final sanitization for Salesforce fields (e.g. multi-select picklists)
            for (const fieldDefinition of configuredFieldDefinitions) {
                const mapping = fieldDefinition.sfApiName
                    ? {
                        objectName: fieldDefinition.sfObjectName || "Case",
                        apiName: fieldDefinition.sfApiName,
                    }
                    : getSalesforceMappingForField(fieldDefinition, caseType);

                if (mapping?.apiName) {
                    const targetData = mapping.objectName === "Case" ? commonFields : childFields;
                    if (targetData && targetData[mapping.apiName]) {
                        let val = targetData[mapping.apiName];
                        if (fieldDefinition.sfMultiSelect) {
                            if (Array.isArray(val)) {
                                targetData[mapping.apiName] = val.map(v => v.toString().trim()).filter(Boolean).join(";");
                            } else if (typeof val === "string") {
                                targetData[mapping.apiName] = val.split(/;\s*|,\s*/).map(v => v.trim()).filter(Boolean).join(";");
                            }
                        }
                    }
                }
            }

            // Get policyNumber and policyId values
            let selectedPolicyId = "";
            let selectedPolicyNumber = "";
            if (fields.policyId) {
                const selectedPolicy = policies?.find(p => (p.policyId || p.id) === fields.policyId);
                if (selectedPolicy) {
                    selectedPolicyId = selectedPolicy.policyId || selectedPolicy.id || "";
                    selectedPolicyNumber = selectedPolicy.policyNumber || "";
                } else {
                    selectedPolicyId = "";
                    selectedPolicyNumber = fields.policyId;
                }
            }

            // Auto-populate child case policy field based on selected policy
            if (selectedPolicyNumber) {
                if (childObjectName === "Case_Hoi_1__c" || childObjectName === "Case_Gen_1__c") {
                    if (!childFields.policy_num__c) {
                        childFields.policy_num__c = selectedPolicyNumber;
                    }
                } else if (childObjectName === "Case_Aut_1__c") {
                    if (!commonFields.Auto_Policy_Number__c) {
                        commonFields.Auto_Policy_Number__c = selectedPolicyNumber;
                    }
                }
            }

            const outlookMeta = getOutlookAnalysisMetadata(mailboxContext, selectedMessageId);

            // Always set Case Origin as "Email" for Outlook cases
            commonFields.Origin = "Email";
            commonFields.AccountId = sfContext?.accountId || "";

            // Validate and sanitize ContactId before sending to Salesforce:
            // Must be a valid 15 or 18-character Salesforce ID (typically starting with "003").
            // If it's a name or invalid format, try resolving against sfContext.contacts, or delete it to avoid MALFORMED_ID.
            if (commonFields.ContactId) {
                const isSalesforceId = /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(commonFields.ContactId);
                if (!isSalesforceId) {
                    const matchedContact = (sfContext?.contacts || []).find(
                        (c) => (c.name || "").toLowerCase() === String(commonFields.ContactId).toLowerCase()
                    );
                    if (matchedContact && matchedContact.contactId) {
                        commonFields.ContactId = matchedContact.contactId;
                    } else {
                        delete commonFields.ContactId;
                    }
                }
            }

            const payload = {
                ParentCaseData: commonFields,
                ChildCaseData: childObjectName ? {
                    childObjectName,
                    ...childFields,
                } : null,
                policyId: selectedPolicyId,
                policyNumber: selectedPolicyNumber,
                accountId: sfContext?.accountId || "",
                attachments: [], // Will populate below
                instance_url: instanceUrl,
                access_token: token,
                userEmail: outlookMeta.userEmail || "",
            };

            // Fetch selected attachments content
            const selectedAtts = attachments.filter(a => a.selected);
            let processedAttachments = [];
            if (selectedAtts.length > 0) {
                const attachmentsContent = await Promise.all(selectedAtts.map(async (att) => {
                    try {
                        const content = await new Promise((resolve, reject) => {
                            Office.context.mailbox.item.getAttachmentContentAsync(att.id, (result) => {
                                if (result.status === Office.AsyncResultStatus.Succeeded) {
                                    resolve(result.value.content);
                                } else {
                                    reject(new Error(result.error.message));
                                }
                            });
                        });
                        return {
                            name: att.name,
                            contentType: att.contentType,
                            content: content // This is base64 for file attachments in Outlook
                        };
                    } catch (err) {
                        console.error(`Failed to get content for ${att.name}:`, err);
                        return null;
                    }
                }));
                processedAttachments = attachmentsContent.filter(Boolean);
            }

            // Always generate and attach the original email as a .eml file
            try {
                const item = Office.context?.mailbox?.item;
                if (item) {
                    const getEmailBody = () => {
                        return new Promise((resolve) => {
                            item.body.getAsync("text", (result) => {
                                if (result.status === Office.AsyncResultStatus.Succeeded) {
                                    resolve(result.value);
                                } else {
                                    resolve("Body text could not be retrieved.");
                                }
                            });
                        });
                    };

                    const bodyText = await getEmailBody();
                    
                    const emlLines = [];
                    emlLines.push(`From: ${item.from?.displayName || ""} <${item.from?.emailAddress || ""}>`);
                    emlLines.push(`To: ${(item.to || []).map(r => `${r.displayName || ""} <${r.emailAddress}>`).join(", ")}`);
                    if (item.cc && item.cc.length > 0) {
                        emlLines.push(`Cc: ${item.cc.map(r => `${r.displayName || ""} <${r.emailAddress}>`).join(", ")}`);
                    }
                    emlLines.push(`Subject: ${item.subject || ""}`);
                    emlLines.push(`Date: ${item.dateTimeCreated ? new Date(item.dateTimeCreated).toUTCString() : new Date().toUTCString()}`);
                    emlLines.push("MIME-Version: 1.0");
                    emlLines.push("Content-Type: text/plain; charset=UTF-8");
                    emlLines.push("");
                    emlLines.push(bodyText);
                    
                    const emlContent = emlLines.join("\r\n");
                    const base64Eml = btoa(unescape(encodeURIComponent(emlContent)));
                    
                    processedAttachments.push({
                        name: "original_email.eml",
                        contentType: "message/rfc822",
                        content: base64Eml
                    });
                }
            } catch (emlErr) {
                console.error("Failed to append EML attachment", emlErr);
            }

            payload.attachments = processedAttachments;
            console.log("checking payload", payload)

            if (!isCurrentMessage(requestMessageId)) {
                return;
            }

            const response = await apiService.createCase(payload);

            if (!isCurrentMessage(requestMessageId)) {
                return;
            }

            if (response.error) {
                setError(`Failed to create case: ${response.details || response.error}`);
            } else {
                setCaseResult(response);
                cachedAiState.caseResult = response;
                updateSubmitted(true);

                // Generate follow-up draft IMMEDIATELY if there are missing fields
                if (dynamicMissing.length > 0 && isCurrentMessage(requestMessageId)) {
                    generateFollowupDraft();
                }
            }
        } catch (err) {
            if (isCurrentMessage(requestMessageId)) {
                console.error("Create case failed:", err);
                setError("Failed to create case due to a network error.");
            }
        } finally {
            if (isCurrentMessage(requestMessageId)) {
                setCreatingCase(false);
            }
        }
    };

        const generateFollowupDraft = async () => {
        if (cachedAiState.followupDraft) return; // Already generated

        const metadata = getOutlookAnalysisMetadata(mailboxContext, selectedMessageId);
        const requestMessageId = currentMessageId || metadata.messageId || "";
        metadata.messageId = requestMessageId;

        setDraftLoading(true);
        try {
            const contactName = sfContext?.contacts?.[0]?.name || "there";

            const response = await apiService.generateFollowup({
                messageId: metadata.messageId,

                userEmail: metadata.userEmail,
                contactName,
                caseType: fields.caseType,
                description: fields.description,
                missingFields: dynamicMissing,
                filledFields: Object.fromEntries(
                    Object.entries(fields).filter(([, v]) => v !== "" && v !== null && v !== undefined)
                ),
            });

            if (!isCurrentMessage(requestMessageId)) {
                return;
            }

            const draft = response.draft || "Could not generate draft.";
            setFollowupDraft(draft);
            cachedAiState.followupDraft = draft;

        } catch (err) {
            if (isCurrentMessage(requestMessageId)) {
                console.error("Follow-up draft failed:", err);
                setFollowupDraft("Failed to generate follow-up email.");
            }
        } finally {
            if (isCurrentMessage(requestMessageId)) {
                setDraftLoading(false);
            }
        }
    };

    const handleStartOver = () => {
        updateAnalyzed(false);
        updateSubmitted(false);
        updateFields(INITIAL_FIELDS);
        setFieldSources(INITIAL_SOURCES);
        cachedAiState.sources = INITIAL_SOURCES;
        setCaseResult(null);
        setFollowupDraft("");
        setDraftLoading(false);
        cachedAiState.caseResult = null;
        cachedAiState.followupDraft = "";
        updateAttachments([]);
        updateAnalyseAttachments(false);
        // Note: we don't reset cachedAiState.accountId/messageId here
        // because they are used to detect when the selected email changes.
    };

    const handleFieldChange = (key, value) => {
        updateFields((prev) => ({ ...prev, [key]: value }));
        updateSources(key, "user");
    };

    const handleToggleAttachment = (id) => {
        updateAttachments(attachments.map(att =>
            att.id === id ? { ...att, selected: !att.selected } : att
        ));
    };

    // Dynamically compute field counts based on the current case type
    const currentTypeFields = getFieldsForCaseType(fields.caseType);
    const allRelevantKeys = [
        ...Object.keys(INITIAL_FIELDS),
        ...STANDARD_HEADER_FIELDS.map((f) => f.key),
        ...currentTypeFields.map((f) => f.key),
    ];
    const totalFields = allRelevantKeys.length;

    const filledCount = allRelevantKeys.filter((key) => {
        const val = fields[key];
        if (typeof val === "boolean") return true;
        return val !== "" && val !== null && val !== undefined;
    }).length;

    // Build missing fields list with human-readable labels
    const hasContacts = Array.isArray(sfContext?.contacts) && sfContext.contacts.length > 0;
    const hasPolicies = Array.isArray(policies) && policies.length > 0;
    const commonMissing = [
        !fields.subject && "Subject",
        (!fields.contactId && hasContacts) && "Contact",
        (!fields.policyId && hasPolicies) && "Related Policy",
        !fields.caseType && "Case Type",
        !fields.priority && "Priority",
        !fields.description && "Description",
    ].filter(Boolean);

    const standardMissing = STANDARD_HEADER_FIELDS
        .filter((f) => !fields[f.key])
        .map((f) => f.label);

    const typeSpecificMissing = currentTypeFields
        .filter((f) => !fields[f.key])
        .map((f) => f.label);

    const dynamicMissing = [...commonMissing, ...standardMissing, ...typeSpecificMissing];
    const missingCount = dynamicMissing.length;

    return (
        <div className="ai-analysis-wrapper">

            {/* ══════════════════════════════════════
                INTRO CARD — only before analysis
                ══════════════════════════════════════ */}
            {/* ══════════════════════════════════════
                MAIN CARD
                ══════════════════════════════════════ */}
            {!submitted && !isDraftOpen && (
                <div className="card">
                    <div className="card-header">
                        <span className="card-header-icon">🤖</span>
                        <h3 className="card-header-title">AI Analysis</h3>
                    </div>

                    <div className="analysis-divider" />

                    {!analyzed ? (
                        <div className="analysis-body">
                            {loading ? (
                                <div className="analysis-loading-container">
                                    <div className="large-spinner" />
                                    <div className="loading-text-main">Analyzing email...</div>
                                    <div className="loading-text-sub">Fetching Case fields from Salesforce</div>
                                </div>
                            ) : (
                                <>
                                    <p className="analysis-intro">
                                        Choose an AI action for this email:
                                    </p>
                                    <ul className="analysis-list">
                                        <li>Extract &amp; prepare Salesforce Case fields &amp; attachments</li>
                                        <li>Generate instant client email replies without creating a case</li>
                                    </ul>

                                    {/* ── Analyse Attachments Toggle ── */}
                                    <div className="analyse-attachments-toggle">
                                        <div className="toggle-left">
                                            <span className="toggle-icon">📎</span>
                                            <div className="toggle-text">
                                                <span className="toggle-label">Analyse Attachments</span>
                                                <span className="toggle-desc">
                                                    {analyseAttachments
                                                        ? "Attachments will be sent to AI for analysis"
                                                        : "Enable to include attachments in AI analysis"}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            className={`toggle-switch ${analyseAttachments ? "toggle-on" : ""}`}
                                            onClick={() => updateAnalyseAttachments(!analyseAttachments)}
                                            role="switch"
                                            aria-checked={analyseAttachments}
                                            aria-label="Analyse Attachments"
                                        >
                                            <span className="toggle-knob" />
                                        </button>
                                    </div>

                                    {error && (
                                        <div className="error-message">
                                            <span className="error-icon">⚠️</span>
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        className="analyze-btn"
                                        onClick={() => handleAnalyze(false)}
                                    >
                                        <span className="cart-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="16" y1="13" x2="8" y2="13" />
                                                <line x1="16" y1="17" x2="8" y2="17" />
                                                <polyline points="10 9 9 9 8 9" />
                                            </svg>
                                        </span>
                                        Analyze &amp; Prepare Case
                                    </button>

                                    <button
                                        type="button"
                                        className="email-reply-launch-button"
                                        onClick={() => setIsDraftOpen(true)}
                                    >
                                        <span className="cart-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                <polyline points="22,6 12,13 2,6" />
                                            </svg>
                                        </span>
                                        Draft Email Reply
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="analysis-body" style={{ padding: "0" }}>
                            <CaseForm
                                fields={fields}
                                handleFieldChange={handleFieldChange}
                                filledCount={filledCount}
                                missingCount={missingCount}
                                dynamicMissing={dynamicMissing}
                                onSubmit={handleCreateCase}
                                creatingCase={creatingCase}
                                sfContext={sfContext}
                                policies={policies}
                                fieldSources={fieldSources}
                                attachments={attachments}
                                onToggleAttachment={handleToggleAttachment}
                                loading={loading}
                                onAnalyzeAttachmentsAlso={() => handleAnalyze(true)}
                                analyseAttachments={analyseAttachments}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════
                SUCCESS & DRAFT SECTION
                ══════════════════════════════════════ */}
            {submitted && (
                <CaseSuccess
                    caseResult={caseResult}
                    fields={fields}
                    filledCount={filledCount}
                    totalFields={totalFields}
                    dynamicMissing={dynamicMissing}
                    onStartOver={handleStartOver}
                    sfContext={sfContext}
                    followupDraft={followupDraft}
                    draftLoading={draftLoading}
                    updateFollowupDraft={updateFollowupDraft}
                />
            )}

            <EmailReplyDraft
                currentMessageId={currentMessageId}
                mailboxContext={mailboxContext}
                isOpen={isDraftOpen}
                onOpenChange={setIsDraftOpen}
                analyseAttachments={analyseAttachments}
                onToggleAnalyseAttachments={updateAnalyseAttachments}
            />

        </div>
    );
};

export default AIAnalysis;
